import { getTrackingProductId } from '@features/products/utils'
import { centAmountToAmountV2 } from '@lib/format-price'
import { graphql } from '@lib/graphql'
import { execute } from '@lib/graphql/execute'
import {
  CartLineItemFragment,
  CartLineItemsRemoveInput,
} from '@lib/graphql/graphql'
import { MutationConfig } from '@lib/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CurrencyCodes,
  pushRemoveFromBasketTrackingEvent,
} from '@utils/tracking-utils'
import { cartsQueryKeys } from '../query-keys'
import { getCartIdFromCookies } from '../utils'

const cartRemoveLineItemsMutationDocument = graphql(`
  mutation CartRemoveLineItems($input: CartLineItemsRemoveInput!) {
    cartLineItemsRemove(input: $input) {
      cart {
        ...CartDetail
      }
    }
  }
`)

export const cartRemoveLineItems = async (input: CartLineItemsRemoveInput) => {
  return await execute(cartRemoveLineItemsMutationDocument, { input })
}

export async function removeCartItem(lineItem: CartLineItemFragment) {
  const cartId = getCartIdFromCookies()

  // add item to cart

  const response = await cartRemoveLineItems({
    cartId,
    lineItemIds: [lineItem.id],
  })
  return {
    cart: response?.cartLineItemsRemove.cart,
    removedLineItem: lineItem,
  }
}

export const useCartRemoveLineItem = (
  mutationConfig?: MutationConfig<typeof removeCartItem>,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    mutationFn: removeCartItem,
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(cartsQueryKeys.all, data.cart)

      const removedLineItem = data.removedLineItem
      pushRemoveFromBasketTrackingEvent({
        currencyCode: data.cart.currency as CurrencyCodes,
        products: [
          {
            id: getTrackingProductId(
              removedLineItem.variant?.product.id,
              removedLineItem.variant?.product?.metadata,
            ),
            name: removedLineItem.productName,
            variant: removedLineItem.variantName,
            category: removedLineItem.variant.product?.tags
              ?.find((tag) => tag.startsWith('type_'))
              ?.split('_')[1]
              .replace(/-/g, ''),
            brand: removedLineItem?.variant.product.vendor,
            quantity: removedLineItem.quantity,
            price: centAmountToAmountV2(
              removedLineItem.unitPrice.discountedPrice
                ? removedLineItem.unitPrice.discountedPrice.value
                : removedLineItem.unitPrice.value,
            )?.toString(),
            dimension1: removedLineItem.sku,
          },
        ],
        trackingList: 'basket',
      })

      onSuccess?.(data, variables, context)
    },
    ...restConfig,
  })
}
